/**
 * Guard Locations Page
 *
 * Enhanced dispatcher dashboard showing:
 * - Real-time guard locations on a map
 * - Dashboard statistics (on duty, patrolling, completed, alerts)
 * - Activity feed with recent events
 * - Actionable alerts bar
 *
 * Uses a socket-first architecture: Socket.IO provides real-time updates,
 * React Query polls at 2-minute intervals as a fallback consistency check.
 * Dashboard stats are derived client-side from guards + alerts cache.
 */

import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Snackbar,
  Link,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { GuardLocationDashboard } from '../../containers/pages/CANguard/components/guardLocation';
import { transformGuardData } from '../../containers/pages/CANguard/components/guardLocation/utils';
import {
  useGuardLocations,
  useDerivedStats,
  useActivityFeed,
  useDispatchAlerts,
  useDispatchSocket,
  mapToActivityEvent,
  GUARD_LOCATIONS_QUERY_KEY,
  ACTIVITY_FEED_QUERY_KEY,
  DISPATCH_ALERTS_QUERY_KEY,
  PATROL_DETAIL_QUERY_KEY,
  CAMERA_LOCATIONS_QUERY_KEY,
  INCIDENT_LOCATIONS_QUERY_KEY,
  PatrolStartedEvent,
  PatrolCompletedEvent,
  CheckpointEvent,
  LocationUpdateEvent,
  AlertEvent,
  GuardArrivedEvent,
  GuardDepartedEvent,
  VmsIncidentAlertEvent,
  AlertDismissedEvent,
  AlertResolvedEvent,
  VmsIncidentStatusChangedEvent,
} from '../../containers/pages/CANguard/hooks/dispatch';
import { AlertAction, DispatchAlert, AlertCoordinates } from '../../containers/pages/CANguard/components/guardLocation/AlertsBar';
import { ActivityEvent, isClickableEvent } from '../../containers/pages/CANguard/components/guardLocation/ActivityFeed';
import PatrolDetailModal, { PatrolStatus } from '../../containers/pages/CANguard/components/guardLocation/PatrolDetailModal';
import GuardSummaryModal from '../../containers/pages/CANguard/components/guardLocation/GuardSummaryModal';
import CameraStreamModal from '../../containers/pages/CANguard/components/guardLocation/CameraStreamModal';
import { GuardLocation, GuardLocationRaw, CameraLocation } from '../../containers/pages/CANguard/components/guardLocation/types';
import { ShiftPatrol, GuardContactResponse, fetchGuardContact, dismissVmsAlert as dismissVmsAlertApi } from '../../api/dispatch';
import { getIncident, dispatchIncident } from '../../api/canvms/incidents';
import type { IncidentDetail, DispatchIncidentInput } from '../../containers/pages/CANvision/types/canvision.types';
import DispatchDialog from '../../containers/pages/CANvision/components/IncidentDetail/DispatchDialog';
import { displayErrorMessage, displayWarningMessage, displaySuccessMessage } from '../../redux/notificationBar/notificationBarSlice';
import { ROUTE_SCHEDULER, ROUTE_CONTACTS } from '../../constants/routerPath';
import { useCameraLocations } from '../../containers/pages/CANguard/hooks/dispatch/useCameraLocations';

// ============================================
// Types for Patrol Detail Modal
// ============================================

interface SelectedPatrol {
  scheduleAssignmentId: number | null;
  patrolId: number | null;
  guardName: string;
  siteName: string;
  patrolStatus: PatrolStatus;
  startedAt: string | null;
}

function GuardLocationsPage() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ============================================
  // Patrol Detail Modal State
  // ============================================
  const [patrolModalOpen, setPatrolModalOpen] = useState(false);
  const [selectedPatrol, setSelectedPatrol] = useState<SelectedPatrol | null>(null);

  // ============================================
  // Guard Summary Modal State
  // ============================================
  const [guardSummaryOpen, setGuardSummaryOpen] = useState(false);
  const [selectedGuard, setSelectedGuard] = useState<GuardLocation | null>(null);

  // ============================================
  // Contact Dialog State
  // ============================================
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactGuardInfo, setContactGuardInfo] = useState<{
    guardId: number;
    guardName: string;
    siteName?: string;
  } | null>(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactData, setContactData] = useState<GuardContactResponse | null>(null);

  // ============================================
  // Emergency Alert Modal State
  // ============================================
  const [emergencyModalOpen, setEmergencyModalOpen] = useState(false);
  const [emergencyAlert, setEmergencyAlert] = useState<DispatchAlert | null>(null);
  const [acknowledgedEmergencies, setAcknowledgedEmergencies] = useState<Set<string>>(new Set());
  const [emergencyIndex, setEmergencyIndex] = useState(0);
  const [emergencyAlertsList, setEmergencyAlertsList] = useState<DispatchAlert[]>([]);

  // ============================================
  // Emergency Audio Ref
  // ============================================
  const emergencyAudioRef = useRef<HTMLAudioElement | null>(null);

  // ============================================
  // Alert Dismiss Undo / Confirmation State
  // ============================================
  const [pendingDismiss, setPendingDismiss] = useState<{
    alertId: string;
    alert: DispatchAlert;
    timeoutId: ReturnType<typeof setTimeout>;
  } | null>(null);
  const [dismissSnackbarOpen, setDismissSnackbarOpen] = useState(false);
  const [criticalDismissDialogOpen, setCriticalDismissDialogOpen] = useState(false);
  const [criticalDismissAlert, setCriticalDismissAlert] = useState<{
    alertId: string;
    alert: DispatchAlert;
  } | null>(null);

  // ============================================
  // Complete Patrol Confirmation State
  // ============================================
  const [completeConfirmDialogOpen, setCompleteConfirmDialogOpen] = useState(false);
  const [completeConfirmAlert, setCompleteConfirmAlert] = useState<{
    alertId: string;
    alert: DispatchAlert;
  } | null>(null);

  // ============================================
  // Patrol Modal Source Tracking (back navigation)
  // ============================================
  const [patrolModalSource, setPatrolModalSource] = useState<'activity' | 'guardSummary' | null>(null);
  const [guardBeforePatrol, setGuardBeforePatrol] = useState<GuardLocation | null>(null);

  // ============================================
  // VMS Dispatch Dialog State
  // ============================================
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [dispatchIncidentData, setDispatchIncidentData] = useState<IncidentDetail | null>(null);

  // ============================================
  // VMS Camera Stream Modal State
  // ============================================
  const [alertCameraStreamOpen, setAlertCameraStreamOpen] = useState(false);
  const [alertStreamCamera, setAlertStreamCamera] = useState<CameraLocation | null>(null);

  // Camera locations (shared cache with Dashboard's useCameraLocations)
  const { cameras: cameraLocations } = useCameraLocations();

  // Fetch guard locations with enhanced patrol data
  const {
    guards,
    isLoading: guardsLoading,
    isFetching: guardsFetching,
    isError: guardsError,
    error: guardsErrorObj,
    dataUpdatedAt: guardsUpdatedAt,
    refetch: refetchGuards,
  } = useGuardLocations({ activeOnly: false, staleMinutes: 120 });

  // Fetch activity feed
  const {
    events: activityEvents,
    isLoading: activityLoading,
    isError: activityError,
    error: activityErrorObj,
    dataUpdatedAt: activityUpdatedAt,
    refetch: refetchActivity,
  } = useActivityFeed({ limit: 50 });

  // Fetch alerts with dismiss/complete mutations
  const {
    alerts,
    isLoading: alertsLoading,
    dismissAlert,
    isDismissing,
    completePatrol,
    isCompleting,
  } = useDispatchAlerts();

  // Transform raw guards to include derived status fields
  const transformedGuards = React.useMemo(() => {
    return transformGuardData(guards);
  }, [guards]);

  // Derive dashboard stats from guards + alerts cache (no separate polling)
  const { stats, onPatrolCompleted } = useDerivedStats({
    guards: transformedGuards,
    alerts: alerts ?? [],
  });

  // ============================================
  // Emergency Audio Helpers
  // ============================================

  // Pre-warm audio context on first user interaction to bypass autoplay restrictions
  const audioUnlockedRef = useRef(false);
  useEffect(() => {
    const unlock = () => {
      if (audioUnlockedRef.current) return;
      const silent = new Audio('/sounds/emergency-alert.mp3');
      silent.volume = 0;
      silent.play().then(() => {
        silent.pause();
        audioUnlockedRef.current = true;
      }).catch(() => {});
    };
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  const playEmergencySound = useCallback((loop = false) => {
    try {
      // Stop any existing audio first to prevent orphaned instances
      if (emergencyAudioRef.current) {
        emergencyAudioRef.current.pause();
        emergencyAudioRef.current.currentTime = 0;
      }
      const audio = new Audio('/sounds/emergency-alert.mp3');
      audio.loop = loop;
      audio.play().catch((err) => {
        console.warn('[EmergencySound] Autoplay blocked:', err.message);
      });
      emergencyAudioRef.current = audio;
    } catch {
      // Ignore audio errors (autoplay restrictions, missing file, etc.)
    }
  }, []);

  const stopEmergencySound = useCallback(() => {
    if (emergencyAudioRef.current) {
      emergencyAudioRef.current.pause();
      emergencyAudioRef.current.currentTime = 0;
      emergencyAudioRef.current = null;
    }
  }, []);

  // ============================================
  // Emergency Alert Detection
  // ============================================

  // Show modal for any unacknowledged emergency alerts
  useEffect(() => {
    if (!alerts || alerts.length === 0) return;

    // Find emergency alerts that haven't been acknowledged
    // Detection: type is 'emergency_sos' OR (critical severity with SOS-related message)
    const emergencyAlerts = alerts.filter((alert) => {
      if (acknowledgedEmergencies.has(alert.alertId)) return false;

      // Direct type match
      if (alert.type === 'emergency_sos') return true;

      // Fallback: critical alerts with SOS indicators in message
      if (alert.severity === 'critical') {
        const messageLower = (alert.message || '').toLowerCase();
        if (
          messageLower.includes('emergency sos') ||
          messageLower.includes('sos alert') ||
          messageLower.includes('emergency button')
        ) {
          return true;
        }
      }

      return false;
    });

    // Show the first unacknowledged emergency alert with queue tracking
    if (emergencyAlerts.length > 0 && !emergencyModalOpen) {
      setEmergencyAlertsList(emergencyAlerts);
      setEmergencyIndex(0);
      setEmergencyAlert(emergencyAlerts[0]);
      setEmergencyModalOpen(true);
      playEmergencySound(true); // Loop until acknowledged
    }
  }, [alerts, acknowledgedEmergencies, emergencyModalOpen, playEmergencySound]);

  // Handle emergency alert acknowledgment — advance to next or close
  const handleAcknowledgeEmergency = useCallback(() => {
    if (emergencyAlert) {
      setAcknowledgedEmergencies((prev) => new Set(prev).add(emergencyAlert.alertId));

      // Check if there are more unacknowledged emergencies in the list
      const nextIndex = emergencyIndex + 1;
      if (nextIndex < emergencyAlertsList.length) {
        // Advance to next emergency
        setEmergencyIndex(nextIndex);
        setEmergencyAlert(emergencyAlertsList[nextIndex]);
      } else {
        // No more emergencies — close modal and stop sound
        setEmergencyModalOpen(false);
        setEmergencyAlert(null);
        setEmergencyAlertsList([]);
        setEmergencyIndex(0);
        stopEmergencySound();
      }
    }
  }, [emergencyAlert, emergencyIndex, emergencyAlertsList, stopEmergencySound]);

  // Handle view guard on emergency (acknowledge and open guard details)
  const handleViewEmergencyGuard = useCallback(() => {
    if (emergencyAlert) {
      // Acknowledge first
      setAcknowledgedEmergencies((prev) => new Set(prev).add(emergencyAlert.alertId));
      setEmergencyModalOpen(false);
      stopEmergencySound();

      // Find guard and open their summary modal
      const guard = guards.find((g) => g.guardId === emergencyAlert.guardId);
      if (guard) {
        // Open Guard Summary modal directly
        setSelectedGuard(guard);
        setGuardSummaryOpen(true);
      }

      setEmergencyAlert(null);
      setEmergencyAlertsList([]);
      setEmergencyIndex(0);
    }
  }, [emergencyAlert, guards, stopEmergencySound]);

  // ============================================
  // Socket.IO Real-time Integration (Issue 5.2)
  // ============================================

  /**
   * Helper to add event to activity feed cache (optimistic update)
   * Decision: Prepend new events and limit to 50 to prevent unbounded growth
   */
  const addEventToFeed = useCallback(
    (event: ActivityEvent) => {
      queryClient.setQueryData(
        ACTIVITY_FEED_QUERY_KEY,
        (old: { events: ActivityEvent[] } | undefined) => {
          if (!old) return { events: [event] };
          // Dedupe by eventId, prepend new event, limit to 50
          const existingIds = new Set(old.events.map((e) => e.eventId));
          if (existingIds.has(event.eventId)) return old;
          return {
            ...old,
            events: [event, ...old.events].slice(0, 50),
          };
        }
      );
    },
    [queryClient]
  );

  /**
   * Helper to update guard's patrol status in cache
   */
  const updateGuardInCache = useCallback(
    (
      guardId: number,
      updater: (guard: GuardLocationRaw) => GuardLocationRaw
    ) => {
      queryClient.setQueryData(
        GUARD_LOCATIONS_QUERY_KEY,
        (old: { guards: GuardLocationRaw[] } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            guards: old.guards.map((guard) =>
              guard.guardId === guardId ? updater(guard) : guard
            ),
          };
        }
      );
    },
    [queryClient]
  );

  // Socket event handlers
  const handlePatrolStarted = useCallback(
    (event: PatrolStartedEvent) => {
      // Add to activity feed
      addEventToFeed(mapToActivityEvent('patrol_started', event));

      // Update guard's patrol status
      updateGuardInCache(event.guardId, (guard) => ({
        ...guard,
        currentPatrol: guard.currentPatrol
          ? {
              ...guard.currentPatrol,
              patrolStatus: 'in_progress',
              startedAt: event.timestamp,
            }
          : null,
      }));
    },
    [addEventToFeed, updateGuardInCache]
  );

  const handlePatrolCompleted = useCallback(
    (event: PatrolCompletedEvent) => {
      // Add to activity feed
      addEventToFeed(mapToActivityEvent('patrol_completed', event));

      // Update guard's patrol status
      updateGuardInCache(event.guardId, (guard) => ({
        ...guard,
        currentPatrol: guard.currentPatrol
          ? {
              ...guard.currentPatrol,
              patrolStatus: 'completed',
              checkpointsCompleted: event.checkpointsCompleted,
              checkpointsTotal: event.checkpointsTotal,
            }
          : null,
      }));

      // Increment derived completed patrols count
      onPatrolCompleted();
    },
    [addEventToFeed, updateGuardInCache, onPatrolCompleted]
  );

  const handleCheckpointCompleted = useCallback(
    (event: CheckpointEvent) => {
      // Add to activity feed
      addEventToFeed(mapToActivityEvent('checkpoint_completed', event));

      // Update guard's checkpoint progress
      updateGuardInCache(event.guardId, (guard) => ({
        ...guard,
        currentPatrol: guard.currentPatrol
          ? {
              ...guard.currentPatrol,
              checkpointsCompleted: event.checkpointsCompleted,
              checkpointsTotal: event.checkpointsTotal,
            }
          : null,
      }));
    },
    [addEventToFeed, updateGuardInCache]
  );

  const handleLocationUpdated = useCallback(
    (event: LocationUpdateEvent) => {
      // Update guard's location in cache (silent - no feed event)
      updateGuardInCache(event.guardId, (guard) => ({
        ...guard,
        latitude: event.latitude,
        longitude: event.longitude,
        accuracy: event.accuracy,
        batteryPercent: event.batteryPercent,
        timestamp: event.timestamp,
        minutesAgo: 0, // Just updated
      }));
    },
    [updateGuardInCache]
  );

  const handleAlertCreated = useCallback(
    (event: AlertEvent) => {
      // Add alert event to activity feed
      addEventToFeed(mapToActivityEvent('alert', event));

      // Play emergency sound for critical / SOS alerts
      if (event.severity === 'critical' || event.type === 'emergency_sos') {
        playEmergencySound();
      }

      // Show toast notification for alerts
      dispatch(displayWarningMessage(`Alert: ${event.message}`));

      // Optimistic cache update: add new alert to alerts cache
      queryClient.setQueryData(
        DISPATCH_ALERTS_QUERY_KEY,
        (old: { alerts: DispatchAlert[]; totalCount: number } | undefined) => {
          if (!old) return old;
          const newAlert: DispatchAlert = {
            alertId: event.alertId || String(Date.now()),
            type: event.type,
            severity: event.severity,
            guardId: event.guardId,
            guardName: event.guardName,
            message: event.message,
            details: event.details || {},
            createdAt: event.timestamp,
            dismissedAt: null,
          };
          return {
            ...old,
            alerts: [newAlert, ...old.alerts],
            totalCount: old.totalCount + 1,
          };
        }
      );
    },
    [addEventToFeed, dispatch, queryClient, playEmergencySound]
  );

  const handleAlertDismissed = useCallback(
    (event: AlertDismissedEvent) => {
      // Optimistic cache update: remove dismissed alert
      queryClient.setQueryData(
        DISPATCH_ALERTS_QUERY_KEY,
        (old: { alerts: DispatchAlert[]; totalCount: number } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            alerts: old.alerts.filter((a) => Number(a.alertId) !== event.alertId),
            totalCount: Math.max(0, old.totalCount - 1),
          };
        }
      );
    },
    [queryClient]
  );

  const handleAlertResolved = useCallback(
    (event: AlertResolvedEvent) => {
      // Optimistic cache update: remove resolved alert
      queryClient.setQueryData(
        DISPATCH_ALERTS_QUERY_KEY,
        (old: { alerts: DispatchAlert[]; totalCount: number } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            alerts: old.alerts.filter((a) => Number(a.alertId) !== event.alertId),
            totalCount: Math.max(0, old.totalCount - 1),
          };
        }
      );
    },
    [queryClient]
  );

  const handleGuardArrived = useCallback(
    (event: GuardArrivedEvent) => {
      addEventToFeed(mapToActivityEvent('guard_arrived', event));
    },
    [addEventToFeed]
  );

  const handleGuardDeparted = useCallback(
    (event: GuardDepartedEvent) => {
      addEventToFeed(mapToActivityEvent('guard_departed', event));
    },
    [addEventToFeed]
  );

  const handleVmsIncidentAlert = useCallback(
    (event: VmsIncidentAlertEvent) => {
      // Show toast with camera name so dispatcher knows immediately
      const cameraLabel = event.cameraName || 'Camera';
      const severityLabel = event.severity === 'critical' ? 'Critical' : 'Warning';
      dispatch(displayWarningMessage(`${severityLabel} Camera Incident: ${cameraLabel} — ${event.title}`));

      // Invalidate alerts, camera locations, and incident locations to pick up the new incident
      queryClient.invalidateQueries({ queryKey: DISPATCH_ALERTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CAMERA_LOCATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: INCIDENT_LOCATIONS_QUERY_KEY });
    },
    [queryClient, dispatch]
  );

  const handleVmsIncidentStatusChanged = useCallback(
    (event: VmsIncidentStatusChangedEvent) => {
      try {
        // Invalidate caches so map markers and alert badges reflect the new status
        queryClient.invalidateQueries({ queryKey: DISPATCH_ALERTS_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: INCIDENT_LOCATIONS_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: CAMERA_LOCATIONS_QUERY_KEY });

        // Show a brief toast so the dispatcher knows the incident status changed
        const statusLabel = event.newStatus.replace(/_/g, ' ');
        const guardInfo = event.guardName ? ` by ${event.guardName}` : '';
        dispatch(displaySuccessMessage(`Incident #${event.incidentId} ${statusLabel}${guardInfo}`));
      } catch (err) {
        console.error('[GuardLocationsPage] Error handling vms:incident-status-changed:', err);
      }
    },
    [queryClient, dispatch]
  );

  // Connect to Socket.IO for real-time updates
  const { isConnected: isSocketConnected, reconnect: socketReconnect } =
    useDispatchSocket({
      onPatrolStarted: handlePatrolStarted,
      onPatrolCompleted: handlePatrolCompleted,
      onCheckpointCompleted: handleCheckpointCompleted,
      onLocationUpdated: handleLocationUpdated,
      onAlertCreated: handleAlertCreated,
      onGuardArrived: handleGuardArrived,
      onGuardDeparted: handleGuardDeparted,
      onVmsIncidentAlert: handleVmsIncidentAlert,
      onAlertDismissed: handleAlertDismissed,
      onAlertResolved: handleAlertResolved,
      onVmsIncidentStatusChanged: handleVmsIncidentStatusChanged,
    });

  // Calculate average center from guards with valid locations
  // Falls back to Calgary (51.0447, -114.0719) if no valid locations
  const mapCenter = React.useMemo(() => {
    const validGuards = transformedGuards.filter(
      (g) => g.latitude !== 0 && g.longitude !== 0
    );

    if (validGuards.length === 0) {
      return { lat: 51.0447, lng: -114.0719 }; // Calgary default
    }

    const avgLat = validGuards.reduce((sum, g) => sum + g.latitude, 0) / validGuards.length;
    const avgLng = validGuards.reduce((sum, g) => sum + g.longitude, 0) / validGuards.length;

    return { lat: avgLat, lng: avgLng };
  }, [transformedGuards]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refetchGuards();
  }, [refetchGuards]);

  // Handle activity refresh
  const handleActivityRefresh = useCallback(() => {
    refetchActivity();
  }, [refetchActivity]);

  // ============================================
  // Dismiss Undo Handlers
  // ============================================

  /** Actually execute the dismiss API call */
  const executeDismiss = useCallback(
    async (alertId: string, alert: DispatchAlert) => {
      const numericAlertId = parseInt(alertId, 10);
      if (alert.type === 'vms_incident' && alert.details.incidentId) {
        try {
          await dismissVmsAlertApi(alert.details.incidentId);
          queryClient.invalidateQueries({ queryKey: DISPATCH_ALERTS_QUERY_KEY });
          queryClient.invalidateQueries({ queryKey: CAMERA_LOCATIONS_QUERY_KEY });
        } catch (err) {
          console.error('[GuardLocationsPage/executeDismiss] Failed to dismiss VMS alert:', err);
          dispatch(displayErrorMessage('Failed to dismiss VMS alert. Please try again.'));
        }
      } else {
        try {
          await dismissAlert(numericAlertId);
        } catch (err) {
          console.error('[GuardLocationsPage/executeDismiss] Failed to dismiss alert:', err);
          dispatch(displayErrorMessage('Failed to dismiss alert. Please try again.'));
        }
      }
    },
    [dismissAlert, queryClient, dispatch]
  );

  /** Start a pending dismiss with 5-second undo window */
  const startPendingDismiss = useCallback(
    (alertId: string, alert: DispatchAlert) => {
      // Clear any existing pending dismiss
      if (pendingDismiss) {
        clearTimeout(pendingDismiss.timeoutId);
      }

      // Optimistically remove the alert from cache immediately
      queryClient.setQueryData(
        DISPATCH_ALERTS_QUERY_KEY,
        (old: { alerts: DispatchAlert[]; totalCount: number } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            alerts: old.alerts.filter((a) => a.alertId !== alertId),
            totalCount: Math.max(0, old.totalCount - 1),
          };
        }
      );

      const timeoutId = setTimeout(() => {
        executeDismiss(alertId, alert);
        setPendingDismiss(null);
        setDismissSnackbarOpen(false);
      }, 5000);

      setPendingDismiss({ alertId, alert, timeoutId });
      setDismissSnackbarOpen(true);
    },
    [pendingDismiss, executeDismiss, queryClient]
  );

  /** Undo a pending dismiss */
  const handleUndoDismiss = useCallback(() => {
    if (pendingDismiss) {
      clearTimeout(pendingDismiss.timeoutId);

      // Restore the alert back into the cache
      queryClient.setQueryData(
        DISPATCH_ALERTS_QUERY_KEY,
        (old: { alerts: DispatchAlert[]; totalCount: number } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            alerts: [pendingDismiss.alert, ...old.alerts],
            totalCount: old.totalCount + 1,
          };
        }
      );

      setPendingDismiss(null);
      setDismissSnackbarOpen(false);
    }
  }, [pendingDismiss, queryClient]);

  /** Handle dismiss snackbar close (auto-hide or clickaway) */
  const handleDismissSnackbarClose = useCallback(
    (_event: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') return;
      setDismissSnackbarOpen(false);
    },
    []
  );

  /** Confirm critical alert dismiss */
  const handleConfirmCriticalDismiss = useCallback(() => {
    if (criticalDismissAlert) {
      setCriticalDismissDialogOpen(false);
      startPendingDismiss(criticalDismissAlert.alertId, criticalDismissAlert.alert);
      setCriticalDismissAlert(null);
    }
  }, [criticalDismissAlert, startPendingDismiss]);

  /** Cancel critical alert dismiss */
  const handleCancelCriticalDismiss = useCallback(() => {
    setCriticalDismissDialogOpen(false);
    setCriticalDismissAlert(null);
  }, []);

  // ============================================
  // Complete Patrol Confirmation Handlers
  // ============================================

  /** Confirm complete patrol */
  const handleConfirmCompletePatrol = useCallback(async () => {
    if (completeConfirmAlert) {
      const patrolId = completeConfirmAlert.alert.details.patrolId;
      if (patrolId) {
        try {
          await completePatrol(patrolId, 'Completed by dispatcher from dashboard');
        } catch (err) {
          console.error('[GuardLocationsPage/handleConfirmCompletePatrol] Failed to complete patrol:', err);
          dispatch(displayErrorMessage('Failed to complete patrol. Please try again.'));
        }
      }
      setCompleteConfirmDialogOpen(false);
      setCompleteConfirmAlert(null);
    }
  }, [completeConfirmAlert, completePatrol, dispatch]);

  /** Cancel complete patrol */
  const handleCancelCompletePatrol = useCallback(() => {
    setCompleteConfirmDialogOpen(false);
    setCompleteConfirmAlert(null);
  }, []);

  // Handle alert actions
  const handleAlertAction = useCallback(
    async (alertId: string, action: AlertAction, alert: DispatchAlert, _coordinates?: AlertCoordinates) => {
      if (action === 'dismiss') {
        // For critical-severity alerts, show a confirmation dialog first
        if (alert.severity === 'critical') {
          setCriticalDismissAlert({ alertId, alert });
          setCriticalDismissDialogOpen(true);
        } else {
          // Non-critical: use snackbar with undo
          startPendingDismiss(alertId, alert);
        }
      } else if (action === 'view' && alert.type === 'vms_incident') {
        // Open camera live stream for VMS alerts
        const cameraId = alert.details.cameraId;
        if (cameraId) {
          const camera = cameraLocations.find(c => c.id === cameraId);
          if (camera) {
            setAlertStreamCamera(camera);
            setAlertCameraStreamOpen(true);
          } else {
            dispatch(displayWarningMessage('Camera not found on map'));
          }
        }
      } else if (action === 'incidents' && alert.type === 'vms_incident') {
        // Navigate to CANvision incidents page filtered by camera
        const cameraId = alert.details.cameraId;
        const params = cameraId ? `?tab=incidents&cameraId=${cameraId}` : '?tab=incidents';
        navigate(`/canvision${params}`);
      } else if (action === 'dispatch') {
        // Navigate to scheduler with customer/address from camera data
        if (alert.type === 'vms_incident' && alert.details.cameraId) {
          const camera = cameraLocations.find(c => c.id === alert.details.cameraId);
          const params = new URLSearchParams();
          params.set('action', 'newPatrol');
          if (camera?.customer_name) params.set('customerName', camera.customer_name);
          if (camera?.address_title) params.set('addressName', camera.address_title);
          navigate(`${ROUTE_SCHEDULER}?${params.toString()}`);
        }
      } else if (action === 'complete') {
        // Show confirmation dialog before completing patrol
        setCompleteConfirmAlert({ alertId, alert });
        setCompleteConfirmDialogOpen(true);
      } else if (action === 'contact') {
        // Open contact dialog with guard info and fetch phone data
        setContactGuardInfo({
          guardId: alert.guardId,
          guardName: alert.guardName,
          siteName: alert.details.siteName,
        });
        setContactData(null); // Reset previous data
        setContactLoading(true);
        setContactDialogOpen(true);

        // Fetch contact data asynchronously
        fetchGuardContact(alert.guardId)
          .then((data) => {
            setContactData(data);
          })
          .catch((err) => {
            console.error('[GuardLocationsPage/fetchGuardContact] Failed to fetch guard contact:', err);
            dispatch(displayWarningMessage('Could not load contact information for this guard.'));
          })
          .finally(() => {
            setContactLoading(false);
          });
      }
      // 'view' and 'locate' actions for guard alerts are handled in the dashboard component
    },
    [startPendingDismiss, queryClient, dispatch, navigate, cameraLocations]
  );

  // ============================================
  // VMS Dispatch Dialog Handlers
  // ============================================

  const handleDispatchGuard = useCallback(
    async (input: DispatchIncidentInput): Promise<{ patrolId: number } | undefined> => {
      if (!dispatchIncidentData) return undefined;
      try {
        const result = await dispatchIncident(dispatchIncidentData.id, input);
        // Invalidate alerts and camera locations after dispatch
        queryClient.invalidateQueries({ queryKey: DISPATCH_ALERTS_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: CAMERA_LOCATIONS_QUERY_KEY });
        dispatch(displaySuccessMessage('Guard dispatched to incident'));
        return result?.data ? { patrolId: result.data.patrolId } : undefined;
      } catch (err) {
        console.error('[GuardLocationsPage/handleDispatchGuard] Failed to dispatch guard:', err);
        throw err;
      }
    },
    [dispatchIncidentData, queryClient, dispatch]
  );

  const handleDispatchDialogClose = useCallback(() => {
    setDispatchDialogOpen(false);
    setTimeout(() => setDispatchIncidentData(null), 300);
  }, []);

  /** Open DispatchDialog for an incident (used by map IncidentMarker) */
  const handleMapIncidentDispatch = useCallback(async (incident: { id: number }) => {
    try {
      const result = await getIncident(incident.id);
      setDispatchIncidentData(result.data);
      setDispatchDialogOpen(true);
    } catch (err) {
      console.error('[GuardLocationsPage/handleMapIncidentDispatch] Failed to fetch incident for dispatch:', err);
      dispatch(displayWarningMessage('Failed to load incident details. The incident may have been resolved.'));
    }
  }, [dispatch]);

  // ============================================
  // Patrol Detail Modal Handlers
  // ============================================

  /**
   * Handle activity feed item click - opens patrol detail modal
   * Only handles patrol-related events (patrol_started, patrol_completed, checkpoint_completed)
   */
  const handleActivityItemClick = useCallback(
    (event: ActivityEvent) => {
      // Only handle clickable patrol events
      if (!isClickableEvent(event.type)) {
        return;
      }

      // Extract patrol information from event
      const { patrolId, scheduleAssignmentId, siteName, patrolStatus } = event.details;

      // Find guard info from current data
      const guard = guards.find((g) => g.guardId === event.guardId);

      // The patrolId in the event corresponds to scheduleAssignmentId in our system
      // (see dispatchApi.ts transformGuardLocation where patrolId = scheduleAssignmentId)
      // For the modal, we need scheduleAssignmentId to fetch details from the C# API
      let resolvedScheduleAssignmentId = scheduleAssignmentId || patrolId || null;

      // If we couldn't get it from the event, try the guard's current patrol
      if (!resolvedScheduleAssignmentId && guard?.currentPatrol) {
        resolvedScheduleAssignmentId = guard.currentPatrol.patrolId;
      }

      // Need scheduleAssignmentId to fetch details
      if (!resolvedScheduleAssignmentId) {
        console.warn('Activity event missing patrol identifiers:', event);
        return;
      }

      // Determine patrol status based on event type
      let derivedStatus: PatrolStatus = 'in_progress';
      if (event.type === 'patrol_completed') {
        derivedStatus = 'completed';
      } else if (patrolStatus) {
        derivedStatus = patrolStatus;
      } else if (guard?.currentPatrol?.patrolStatus) {
        derivedStatus = guard.currentPatrol.patrolStatus;
      }

      const startedAt = guard?.currentPatrol?.startedAt || null;

      setSelectedPatrol({
        scheduleAssignmentId: resolvedScheduleAssignmentId,
        patrolId: patrolId || resolvedScheduleAssignmentId,
        guardName: event.guardName,
        siteName: siteName || guard?.currentPatrol?.siteName || 'Unknown Site',
        patrolStatus: derivedStatus,
        startedAt,
      });
      setPatrolModalSource('activity');
      setPatrolModalOpen(true);
    },
    [guards]
  );

  /**
   * Handle patrol detail modal close
   * If opened from guard summary, re-open the guard summary modal
   */
  const handlePatrolModalClose = useCallback(() => {
    setPatrolModalOpen(false);

    if (patrolModalSource === 'guardSummary' && guardBeforePatrol) {
      // Re-open guard summary modal for the same guard
      const guardToRestore = guardBeforePatrol;
      setTimeout(() => {
        setSelectedGuard(guardToRestore);
        setGuardSummaryOpen(true);
      }, 300);
    }

    // Delay clearing selected patrol to allow modal close animation
    setTimeout(() => {
      setSelectedPatrol(null);
      setPatrolModalSource(null);
      setGuardBeforePatrol(null);
    }, 300);
  }, [patrolModalSource, guardBeforePatrol]);

  /**
   * Handle complete patrol from modal
   * Wraps the existing completePatrol mutation with additional handling
   */
  const handleCompletePatrolFromModal = useCallback(
    async (patrolId: number, note?: string) => {
      try {
        await completePatrol(patrolId, note);
        dispatch(displaySuccessMessage('Patrol completed successfully'));
        // Invalidate patrol detail cache
        queryClient.invalidateQueries({ queryKey: PATROL_DETAIL_QUERY_KEY });
      } catch (err) {
        console.error('[GuardLocationsPage/handleCompletePatrolFromModal] Failed to complete patrol from modal:', err);
        throw err; // Re-throw so modal can handle the error
      }
    },
    [completePatrol, dispatch, queryClient]
  );

  // ============================================
  // Guard Summary Modal Handlers
  // ============================================

  /**
   * Handle guard view details - opens Guard Summary modal
   * Called from dashboard panel or map marker "View Details" button
   */
  const handleGuardViewDetails = useCallback((guard: GuardLocation) => {
    setSelectedGuard(guard);
    setGuardSummaryOpen(true);
  }, []);

  /**
   * Handle Guard Summary modal close
   */
  const handleGuardSummaryClose = useCallback(() => {
    setGuardSummaryOpen(false);
    // Delay clearing selected guard to allow modal close animation
    setTimeout(() => {
      setSelectedGuard(null);
    }, 300);
  }, []);

  /**
   * Handle clicking a patrol row in Guard Summary modal
   * Opens the Patrol Detail modal for that patrol, with back navigation support
   */
  const handleViewPatrolFromSummary = useCallback(
    (scheduleAssignmentId: number, patrol: ShiftPatrol) => {
      // Store the guard reference so we can restore the summary modal on close
      setGuardBeforePatrol(selectedGuard);
      setPatrolModalSource('guardSummary');

      // Close guard summary modal
      setGuardSummaryOpen(false);

      // Open patrol detail modal with the selected patrol info
      setSelectedPatrol({
        scheduleAssignmentId,
        patrolId: patrol.patrolId,
        guardName: selectedGuard?.name || '',
        siteName: patrol.siteName,
        patrolStatus: patrol.status,
        startedAt: patrol.actualStart,
      });
      setPatrolModalOpen(true);
    },
    [selectedGuard]
  );

  // ============================================
  // Schedule Patrol Navigation Handlers
  // ============================================

  /**
   * Navigate to scheduler to create a new patrol for a guard.
   * Pre-fills guard name (and customer/address if guard has a current patrol).
   */
  const handleSchedulePatrolForGuard = useCallback(
    (guard: GuardLocation) => {
      const params = new URLSearchParams();
      params.set('action', 'newPatrol');
      params.set('contactName', guard.name);
      if (guard.currentPatrol?.customerName) {
        params.set('customerName', guard.currentPatrol.customerName);
      }
      if (guard.currentPatrol?.siteName) {
        params.set('addressName', guard.currentPatrol.siteName);
      }
      navigate(`${ROUTE_SCHEDULER}?${params.toString()}`);
    },
    [navigate]
  );

  /**
   * Navigate to scheduler to create a new patrol at a site.
   * Pre-fills customer and address name.
   */
  const handleSchedulePatrolForSite = useCallback(
    (_addressId: number | null, siteName: string, customerName: string) => {
      const params = new URLSearchParams();
      params.set('action', 'newPatrol');
      if (customerName) params.set('customerName', customerName);
      if (siteName) params.set('addressName', siteName);
      navigate(`${ROUTE_SCHEDULER}?${params.toString()}`);
    },
    [navigate]
  );

  // Show full-page loading only on initial load (no data yet)
  if (guardsLoading && transformedGuards.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '80vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Loading dispatcher dashboard...
        </Typography>
      </Box>
    );
  }

  // Show error state only if no data could be loaded
  if (guardsError && transformedGuards.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '80vh',
          gap: 2,
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Failed to load guard locations
          </Typography>
          <Typography variant="body2">
            {guardsErrorObj?.message || 'An unexpected error occurred'}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <GuardLocationDashboard
        // Guard data
        guards={transformedGuards}
        isLoading={guardsFetching}
        error={guardsError ? (guardsErrorObj?.message || 'Failed to fetch') : null}
        lastUpdate={guardsUpdatedAt ? new Date(guardsUpdatedAt) : null}
        onRefresh={handleRefresh}
        onGuardViewDetails={handleGuardViewDetails}
        initialCenter={mapCenter}
        initialZoom={11}
        // Stats data (derived from guards + alerts cache)
        stats={stats}
        statsLoading={false}
        statsError={null}
        onStatsRetry={() => {}}
        // Activity feed data
        activityEvents={activityEvents}
        activityLoading={activityLoading}
        activityError={activityError ? (activityErrorObj?.message || 'Failed to fetch activity') : null}
        activityLastUpdate={activityUpdatedAt ? new Date(activityUpdatedAt) : null}
        onActivityRefresh={handleActivityRefresh}
        onActivityItemClick={handleActivityItemClick}
        // Alerts data
        alerts={alerts}
        onAlertAction={handleAlertAction}
        // Socket.IO connection status (Issue 5.2)
        isSocketConnected={isSocketConnected}
        onSocketReconnect={socketReconnect}
        // Schedule patrol navigation
        onSchedulePatrolForSite={handleSchedulePatrolForSite}
        // Incident dispatch (opens DispatchDialog from map markers)
        onIncidentDispatch={handleMapIncidentDispatch}
      />

      {/* Guard Summary Modal */}
      <GuardSummaryModal
        open={guardSummaryOpen}
        onClose={handleGuardSummaryClose}
        guard={selectedGuard}
        onViewPatrolDetails={handleViewPatrolFromSummary}
        onSchedulePatrol={handleSchedulePatrolForGuard}
      />

      {/* Patrol Detail Modal */}
      <PatrolDetailModal
        open={patrolModalOpen}
        onClose={handlePatrolModalClose}
        scheduleAssignmentId={selectedPatrol?.scheduleAssignmentId ?? null}
        patrolId={selectedPatrol?.patrolId ?? null}
        guardName={selectedPatrol?.guardName ?? ''}
        siteName={selectedPatrol?.siteName ?? ''}
        patrolStatus={selectedPatrol?.patrolStatus ?? 'in_progress'}
        startedAt={selectedPatrol?.startedAt}
        onCompletePatrol={handleCompletePatrolFromModal}
        isCompleting={isCompleting}
        allowEdit
      />

      {/* Guard Contact Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon color="primary" />
            <Typography variant="h6">Contact Guard</Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setContactDialogOpen(false)}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {contactGuardInfo && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="action" />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Guard Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {contactGuardInfo.guardName}
                  </Typography>
                </Box>
              </Box>
              {contactGuardInfo.siteName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Current Location
                    </Typography>
                    <Typography variant="body1">
                      {contactGuardInfo.siteName}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Phone Number Section */}
              {contactLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="textSecondary">
                    Loading contact information...
                  </Typography>
                </Box>
              ) : contactData && (contactData.phoneWork || contactData.phoneCell) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {contactData.phoneWork && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon color="action" />
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Work Phone
                        </Typography>
                        <Typography
                          component="a"
                          href={`tel:${contactData.phoneWorkDialCode ? `+${contactData.phoneWorkDialCode}` : ''}${contactData.phoneWork.replace(/\D/g, '')}`}
                          variant="body1"
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {contactData.phoneWorkDialCode ? `+${contactData.phoneWorkDialCode} ` : ''}{contactData.phoneWork}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {contactData.phoneCell && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon color="action" />
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Cell Phone
                        </Typography>
                        <Typography
                          component="a"
                          href={`tel:${contactData.phoneCellDialCode ? `+${contactData.phoneCellDialCode}` : ''}${contactData.phoneCell.replace(/\D/g, '')}`}
                          variant="body1"
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {contactData.phoneCellDialCode ? `+${contactData.phoneCellDialCode} ` : ''}{contactData.phoneCell}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  {/* Show email if available even when phone is missing */}
                  {contactData?.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon color="action" />
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Email
                        </Typography>
                        <Typography
                          component="a"
                          href={`mailto:${contactData.email}`}
                          variant="body1"
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {contactData.email}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  <Alert severity="info">
                    <Typography variant="body2">
                      Phone number not available.{' '}
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => {
                          setContactDialogOpen(false);
                          // Navigate to the contact record in admin
                          // Route: ROUTE_CONTACTS (/contacts) with guard's contactId
                          navigate(`${ROUTE_CONTACTS}/${contactGuardInfo.guardId}`);
                        }}
                        sx={{ verticalAlign: 'baseline' }}
                      >
                        View contact record
                      </Link>
                      {' '}to update contact information for this guard.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* VMS Dispatch Dialog */}
      {dispatchIncidentData && (
        <DispatchDialog
          open={dispatchDialogOpen}
          incident={dispatchIncidentData}
          onClose={handleDispatchDialogClose}
          onDispatch={handleDispatchGuard}
        />
      )}

      {/* VMS Camera Stream Modal (from alert "View Camera" action) */}
      <CameraStreamModal
        open={alertCameraStreamOpen}
        camera={alertStreamCamera}
        onClose={() => {
          setAlertCameraStreamOpen(false);
          setAlertStreamCamera(null);
        }}
      />

      {/* Emergency Alert Modal - Requires Acknowledgment */}
      <Dialog
        open={emergencyModalOpen}
        onClose={() => {}} // Prevent closing by clicking outside
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown // Prevent closing with Escape key
        PaperProps={{
          sx: {
            border: '3px solid',
            borderColor: 'error.main',
            boxShadow: (theme) => `0 0 20px ${theme.palette.error.main}80`,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: 'error.main',
            color: 'error.contrastText',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 28 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="span" fontWeight="bold">
              🚨 EMERGENCY SOS ALERT
            </Typography>
            {emergencyAlertsList.length > 1 && (
              <Typography variant="body2" component="div" sx={{ opacity: 0.9 }}>
                Alert {emergencyIndex + 1} of {emergencyAlertsList.length}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {emergencyAlert && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Alert severity="error" icon={false} sx={{ py: 2 }}>
                <Typography variant="body1" fontWeight="medium">
                  {emergencyAlert.message || 'Guard has triggered an emergency SOS alert!'}
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PersonIcon color="primary" sx={{ fontSize: 24 }} />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Guard Name
                  </Typography>
                  <Typography variant="h6">
                    {emergencyAlert.guardName}
                  </Typography>
                </Box>
              </Box>

              {emergencyAlert.details.siteName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LocationOnIcon color="action" sx={{ fontSize: 24 }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Location
                    </Typography>
                    <Typography variant="body1">
                      {emergencyAlert.details.siteName}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AccessTimeIcon color="action" sx={{ fontSize: 24 }} />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Alert Time
                  </Typography>
                  <Typography variant="body1">
                    {new Date(emergencyAlert.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              {/* Show other pending emergency guard names */}
              {emergencyAlertsList.length > 1 && (() => {
                const otherAlerts = emergencyAlertsList.filter(
                  (_, idx) => idx !== emergencyIndex
                );
                if (otherAlerts.length === 0) return null;
                const otherNames = otherAlerts.map((a) => a.guardName).join(', ');
                return (
                  <Alert severity="error" variant="outlined" sx={{ mt: 0.5 }}>
                    <Typography variant="body2">
                      Also pending: {otherNames}
                    </Typography>
                  </Alert>
                );
              })()}

              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Please contact the guard immediately and take appropriate action.
                  Phone number lookup available in employee records.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          {/* Sound button — fallback if autoplay was blocked */}
          {!emergencyAudioRef.current?.paused ? null : (
            <Button
              onClick={() => playEmergencySound(true)}
              variant="outlined"
              color="error"
              startIcon={<VolumeUpIcon />}
              sx={{ mr: 'auto' }}
            >
              Play Alert
            </Button>
          )}
          <Button
            onClick={handleViewEmergencyGuard}
            variant="outlined"
            color="primary"
            startIcon={<LocationOnIcon />}
          >
            View on Map
          </Button>
          <Button
            onClick={handleAcknowledgeEmergency}
            variant="contained"
            color="error"
            size="large"
          >
            Acknowledge Alert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Critical Alert Dismiss Confirmation Dialog */}
      <Dialog
        open={criticalDismissDialogOpen}
        onClose={handleCancelCriticalDismiss}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Dismiss Critical Alert</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure? This is a critical alert. Dismissing it will remove it from your active alerts list.
          </DialogContentText>
          {criticalDismissAlert && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
              <Typography variant="body2" color="error.dark" fontWeight="medium">
                {criticalDismissAlert.alert.message}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCriticalDismiss} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmCriticalDismiss} variant="contained" color="error">
            Dismiss Alert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Patrol Confirmation Dialog */}
      <Dialog
        open={completeConfirmDialogOpen}
        onClose={handleCancelCompletePatrol}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Complete Patrol</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Complete patrol on behalf of{' '}
            <strong>{completeConfirmAlert?.alert.guardName}</strong>
            {completeConfirmAlert?.alert.details.siteName && (
              <>
                {' '}at <strong>{completeConfirmAlert.alert.details.siteName}</strong>
              </>
            )}
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCompletePatrol} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleConfirmCompletePatrol} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dismiss Undo Snackbar */}
      <Snackbar
        open={dismissSnackbarOpen}
        autoHideDuration={5000}
        onClose={handleDismissSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={handleDismissSnackbarClose}
          action={
            <Button color="inherit" size="small" onClick={handleUndoDismiss}>
              Undo
            </Button>
          }
          sx={{ width: '100%' }}
        >
          Alert dismissed
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default GuardLocationsPage;
